import {CoreGameplay} from './gameplay/CoreGameplay';
import {SceneManager} from './managers/SceneManager';
import {PopUpManager, PopUpName} from './managers/PopUpManager';
import {SettingsPopUp} from '../popUps/SettingsPopUp';
import {AbstractPopUp, AbstractPopUpParams} from '../popUps/AbstractPopUp';
import {NoLivesPopUp, NoLivesPopUpParams} from '../popUps/NoLivesPopUp';
import {translate} from '../utils/GameEngine';
import {AnalyticsEventNames} from '../analytics/AnalyticsEventNames';
import {addSignal} from '../utils/EventUtils';
import {Events} from './Events';
import {ConfirmPopUp, ConfirmPopUpParams} from '../popUps/ConfirmPopUp';
import {SaveDataKeys} from './managers/SaveDataKeys';
import {StoryProgressManager} from './managers/saveData/StoryProgressManager';
import {GameAnalytics} from '../analytics/GameAnalytics';
import {CheatPopup, CheatPopupParams} from '../popUps/CheatPopUp';
import {TextStyles} from '../utils/TextStyles';
import {SettingsButtonRound} from '../UI/elements/buttons/SettingsButtonRound';
import {LoadingPopUp} from '../popUps/LoadingPopUp';
import {IAPManager, ProductID} from './managers/IAPManager';
import Container = PIXI.Container;
import Text = PIXI.Text;
import {NoAdsPassButton} from '../UI/elements/buttons/NoAdsPassButton';
import {ThankYouPopup} from '../popUps/ThankYouPopup';
import {FBAdManager} from './managers/abstract/FBAdManager';
import {FacebookManager} from './managers/facebook/FacebookManager';
import {GDAdManager} from './managers/gameDistribution/GDAdManager';
import {AbstractAdManager} from './managers/abstract/AbstractAdManager';

export class CoreController {
    private static _instance: CoreController;
    public readonly INTERSTITIALS_AVAILABLE: boolean = true;

    private gamePlay: CoreGameplay;
    private difficulty: number = 1;
    private levelsInSession: number = 0;
    private levelsLostInSession: number = 0;
    private sessionDuration: number = 0;
    private paused: boolean;
    private consecutiveLosses: number = 0;
    // private dailyRewardButton: DailyRewardIcon;
    private version: string = '2.0.8';
    private endPopUp: AbstractPopUp;
    private settingsButton: SettingsButtonRound;
    private loadingPopup: LoadingPopUp;
    private loadingVisible: boolean = false;
    public levelsSinceLastInterstitial: number = -1;
    private DEV_MODE: boolean = false;
    private noAdsButton: NoAdsPassButton;
    private managerClass: any;
    public adManager: any;

    private constructor() {
        this.addEventsListeners();

        if (!this.isFB() || GameAnalytics.getInstance().isTester()) {
            const version: Text = new Text(this.version, TextStyles.smallTextBlack);
            version.anchor.set(0.5, 0.5);
            version.position.set(SceneManager.gameWidth / 2, SceneManager.gameHeight - 20);
            SceneManager.getInstance().stage.addChild(version);
        }

        if (this.isFB()) {
            const entryPointData: any = FBInstant.getEntryPointData();
            if (entryPointData && entryPointData.payload) {
                GameAnalytics.getInstance().logDesign(AnalyticsEventNames.invite_source + entryPointData.payload);
            }
        }

        // DailyRewardManager.getInstance().init().then((): void => {
        //     this.dailyRewardButton = new DailyRewardIcon(this.onDailyClicked.bind(this));
        //     this.dailyRewardButton.position.set(45, 150);
        // });

        this.noAdsButton = new NoAdsPassButton(this.onNoAdsButtonPressed.bind(this));
        this.noAdsButton.position.set(60, 150);

        this.addSettingsButton();
    }

    public init(devMode: boolean = false, managerClass: any): Promise<void> {
        this.DEV_MODE = devMode;
        this.managerClass = managerClass;
        if (this.managerClass === FacebookManager) {
            // @ts-ignore
            this.adManager = FBAdManager as AbstractAdManager;
        } else {
            // @ts-ignore
            this.adManager = GDAdManager as AbstractAdManager;
        }

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.onGamePaused();
            } else {
                this.onGameResumed();
            }
        });

        return this.managerClass.getInstance().getSaveData(SaveDataKeys.solved).then((data: any): void => {
            if (data) {
                if (data.solved) {
                    this.difficulty = data.solved + 1;
                }
            }
            setTimeout((): void => {
                if (this.difficulty < 129) {
                    // for (let colorIndex: number = 1; colorIndex < 128; colorIndex++) {
                    //     this.gamePlay = new CoreGameplay(colorIndex, this);
                    // }
                    // LevelManager.getInstance().exportToJsonFile();
                    this.playLevel();
                    if (this.isFB()) {
                        this.attemptBotSubscribe();
                    } else {
                        this.adManager.getInstance().showInterstitialAd((): any => {
                            // preroll finished
                        });
                    }
                } else {
                    this.showEndPopup();
                }
            }, 300);
        });
    }

    private onDailyClicked(): void {
        // DailyRewardManager.getInstance().onRewardClicked();
    }

    private startSessionTimer(): void {
        setInterval(() => {
            if (!this.paused) {
                this.sessionDuration++;
            }
        }, 1000);
        // FBInstant.onPause(this.onGamePaused.bind(this));
    }

    private onGamePaused(): void {
        dispatchEvent(new CustomEvent(Events.gamePaused));
        this.paused = true;
    }

    private onGameResumed(): void {
        dispatchEvent(new CustomEvent(Events.gameResumed));
        this.paused = false;
    }

    private resumeTimer(): void {
        if (this.paused) {
            this.paused = false;
        }
    }

    public nextLevel(): void {
        // this.dailyRewardButton.interactive = false;
        this.noAdsButton.interactive = false;
        SceneManager.getInstance().stage.removeChild(this.gamePlay);
        this.gamePlay.destroy();
        this.difficulty++;
        this.playLevel();
        this.unblockSettings();
    }

    private addEventsListeners(): void {
        addSignal(this, Events.showResetPopup, this.showResetPopup.bind(this));
        addSignal(this, Events.levelLost, this.playLevel.bind(this));
        addSignal(this, Events.userInput, this.resumeTimer.bind(this));
        addSignal(this, Events.showCheatPopup, this.showCheatPopup.bind(this));
        addSignal(this, Events.startOver, this.startOver.bind(this));
        addSignal(this, Events.playLoadingAnimation, this.playLoadingAnimation.bind(this));
        addSignal(this, Events.stopLoadingAnimation, this.stopLoadingAnimation.bind(this));
        addSignal(this, Events.interstitialShown, this.onInterstitialShown.bind(this));
    }

    public static getInstance(): CoreController {
        return this._instance || (this._instance = new this());
    }

    private showMainMenu(): void {
        // const FTUE: boolean = StoryProgressManager.getInstance().isFTUE();
        // this.mainMenu = WindowManager.getInstance().getView(WindowName.MainMenu, {FTUE: FTUE}, true) as MainMenu;
        // this.mainMenu.show();
    }

    public onLevelLost(): void {
        // this.dailyRewardButton.interactive = false;
        this.noAdsButton.interactive = false;
        this.levelsLostInSession++;
        this.consecutiveLosses++;
        if (this.consecutiveLosses === 3) {
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.consecutive_losses, true, true, null);
            this.consecutiveLosses = 0;
        }
    }

    public onLevelWon(levelScore: number): void {
        this.consecutiveLosses = 0;
        CoreController.getInstance().getManager().saveData(SaveDataKeys.solved, this.difficulty);
        StoryProgressManager.getInstance().saveLevelsInSession(this.levelsInSession);
        StoryProgressManager.getInstance().saveSessionDuration(this.sessionDuration);
        this.levelsInSession++;
    }

    public playLevel(retry: boolean = false): void {
        if (!retry) {
            this.levelsSinceLastInterstitial++;
        }
        if (this.gamePlay) {
            this.gamePlay.destroy();
        }
        const stage: Container = SceneManager.getInstance().stage;
        this.gamePlay = new CoreGameplay(this.difficulty, this, retry);
        stage.addChild(this.gamePlay);
        // DailyRewardManager.getInstance().checkDailyRewardStatus();
        const timeout: number = this.levelsInSession === 0 ? 200 : 0;
        setTimeout((): void => {
            // stage.addChildAt(this.dailyRewardButton, 0);
            stage.addChildAt(this.settingsButton, 0);
        }, timeout);
        if (this.canShowBannerAd()) {
            if (!FBAdManager.getInstance().hasBannerAd()) {
                FBAdManager.getInstance().showBannerAd();
            } else {
                if (this.difficulty % 2 === 1) {
                    if (!FBAdManager.getInstance().hasBannerAd()) {
                        FBAdManager.getInstance().showBannerAd();
                    }
                }
            }
        }
    }

    private showResetPopup(): void {
        const params: ConfirmPopUpParams = {
            callback: (): void => {
                this.onResetProgressData();
            },
            text: translate('Reset progress', true),
            text2: translate('Do you want to reset your progress?\nYou will go back to level 1'),
            image: 'lose_mascot'
        };
        const resetPopup: ConfirmPopUp = PopUpManager.getInstance().getView(PopUpName.ConfirmPopUp, params) as ConfirmPopUp;
        resetPopup.show();
    }

    private onResetProgressData(): void {
        FBInstant.player.setDataAsync({
            solved: 0,
            numberOfSessions: 0
        });
        StoryProgressManager.getInstance().numberOfSessions = 1;
        this.difficulty = 1;
        this.playLevel();
        SceneManager.getInstance().stage.addChild(this.gamePlay);
    }

    private showSettings(): void {
        const params: AbstractPopUpParams = {
            image: 'settings_penguin_illustration',
            text: translate('Settings', true)
        };
        const settingsPopup: SettingsPopUp = PopUpManager.getInstance().getView(
            PopUpName.SettingsPopUp,
            params,
            true
        ) as SettingsPopUp;
        settingsPopup.show();
    }

    private showNoLives(callback: () => void): void {
        const popUpParams: NoLivesPopUpParams = {
            callback: callback,
            image: 'no_lives_mascot',
            noCloseButton: true,
            text: translate('You have no lives left', true) + '...',
            chapter: this.getCurrentChapter()
        };
        const noLivesPopUp: NoLivesPopUp = PopUpManager.getInstance().getView(
            PopUpName.NoLivesPopUp,
            popUpParams,
            true
        ) as NoLivesPopUp;
        noLivesPopUp.show();
    }

    public getCurrentChapter(): number {
        return 1 + Math.floor((this.difficulty - 1) / 10);
    }

    public getCurrentLevel(): number {
        return this.difficulty;
    }

    public getLevelsInSession(): number {
        return this.levelsInSession;
    }

    public getLevelsLostInSession(): number {
        return this.levelsLostInSession;
    }

    private showCheatPopup(): void {
        // @ts-ignore
        if ((this.isFB() && GameAnalytics && GameAnalytics.getInstance().isTester()) || window.cheat === true) {
            const params: CheatPopupParams = {
                text: 'GO TO LEVEL:',
                callback: this.cheatFunction.bind(this)
            };
            const popup: CheatPopup = PopUpManager.getInstance().getView(PopUpName.CheatPopup, params, true) as CheatPopup;
            popup.show();
        }
    }

    private cheatFunction(level: number): void {
        this.difficulty = level;
        this.playLevel();
    }

    public showEndPopup(): void {
        const params: AbstractPopUpParams = {
            text: translate('Congratulations', true) + '!',
            text2: translate('You finished all\nthe available levels') + '!',
            image: 'win_mascot',
            noCloseButton: true,
        };
        this.endPopUp = PopUpManager.getInstance().getView(PopUpName.EndPopup, params);
        this.endPopUp.show();
    }

    private startOver(): void {
        CoreController.getInstance().getManager().saveData(SaveDataKeys.solved, 15);
        this.difficulty = 15;
        this.playLevel();
        SceneManager.getInstance().stage.addChild(this.gamePlay);
        this.endPopUp.hide(true);
        if (CoreController.getInstance().isFB()) {
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.start_over);
        }
    }

    private addSettingsButton(): void {
        this.settingsButton = new SettingsButtonRound((): void => {
            const params: AbstractPopUpParams = {
                image: 'settings-mascot',
                text: translate('Settings', true),
                onCloseCallback: (): void => {
                    // (this.core as DuelGameplay).resume();
                }
            };
            const settingsPopup: SettingsPopUp = PopUpManager.getInstance().getView(
                PopUpName.SettingsPopUp,
                params,
                true
            ) as SettingsPopUp;
            settingsPopup.show();
            // (this.core as DuelGameplay).pause();
        }, 0.5);
        this.settingsButton.position.set(30, 490);
        this.settingsButton.enable();
    }

    public blockSettings(): void {
        this.settingsButton.disable();
    }

    public unblockSettings(): void {
        this.settingsButton.enable();
    }

    private playLoadingAnimation(): void {
        if (!this.loadingPopup) {
            const params: AbstractPopUpParams = {
                noOverlay: true,
                text: translate('Loading', true) + '...',
                noDefaultAnimations: true,
                noCloseButton: true,
                customBackground: 'donuts_pattern_blur_win',
            };
            this.loadingPopup = PopUpManager.getInstance().getView(PopUpName.Loading, params) as LoadingPopUp;
        }
        if (!this.loadingVisible) {
            this.loadingPopup.show();
            this.loadingVisible = true;
        }
    }

    private stopLoadingAnimation(): void {
        if (this.loadingPopup && this.loadingVisible) {
            this.loadingPopup.hide(true);
            this.loadingVisible = false;
        }
    }

    private onInterstitialShown(): void {
        if (!this.getManager().hasNoAdsPass() && IAPManager.getInstance().IAPAvailable) {
            const stage: Container = SceneManager.getInstance().stage;
            stage.addChildAt(this.noAdsButton, 0);
            this.noAdsButton.interactive = true;
            this.noAdsButton.playAppearAnimation();
        }
    }

    private onNoAdsButtonPressed(): void {
        IAPManager.getInstance().attemptPurchase(
            ProductID.noAdsPass,
            this.onNoAdsPurchaseSuccess.bind(this),
            this.onNoAdsPurchaseFailed.bind(this)
        );
    }

    private onNoAdsPurchaseSuccess(): void {
        this.managerClass.getInstance().buyNoAdsPass();
        SceneManager.getInstance().stage.removeChild(this.noAdsButton);
        this.showThankYouPopUp();
    }

    private onNoAdsPurchaseFailed(error: any): void {
        if (error.code !== 'USER_INPUT') {
            SceneManager.getInstance().stage.removeChild(this.noAdsButton);
        }
    }

    public isDevMode(): boolean {
        return this.DEV_MODE;
    }

    private showThankYouPopUp(): void {
        const params: ConfirmPopUpParams = {
            callback: (): void => {
                //
            },
            text: translate('Thank you') + ', ' + FBInstant.player.getName() + '!',
            text2: translate('You can now enjoy Mater of Donuts without interruptions!'),
            image: 'win_mascot',
            noDiscardButton: true,
            confirmButtonLabel: 'OK'
        };
        const popup: ThankYouPopup = PopUpManager.getInstance().getView(PopUpName.ThankYouPopup, params) as ThankYouPopup;
        popup.show();
    }

    private canShowBannerAd(): boolean {
        let result: boolean = true;
        if (this.isDevMode() || this.managerClass.getInstance().hasNoAdsPass() || this.difficulty < 5) {
            result = false;
        }
        return result;
    }

    private attemptBotSubscribe(): void {
        FBInstant.player.canSubscribeBotAsync().then((can_subscribe: boolean) => {
            if (can_subscribe) {
                console.log(can_subscribe);
                FBInstant.player.subscribeBotAsync().then(() => {
                        GameAnalytics.getInstance().logDesign(AnalyticsEventNames.bot_subscribe_success);
                    }
                ).catch((err: any) => {
                    GameAnalytics.getInstance().logDesign(AnalyticsEventNames.bot_subscribe_fail);
                });
            }
        }).catch((e: any) => console.log(e));
    }

    public getManager(): any {
        return this.managerClass.getInstance();
    }

    public isFB(): boolean {
        return this.managerClass === FacebookManager;
    }
}